import type { OptimizationStatus } from '@/shared/generated'

import React, { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { any, orderBy, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	getOgStatus,
	getOptimizationsMap,
	getStatusBadgeVariant,
	useOptimizationQuery,
	useOptimizationsQuery,
} from '@/entities/optimization'
import {
	useOptimizationGroupQuery,
	useOptimizationGroupsQuery,
} from '@/entities/optimization-group'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Tag } from '@/shared/components/ui/tag'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'

export function OptimizationGroupSwitcher() {
	const optimizationGroupId = useOptimizationGroupId()!
	const optimizationGroupQuery = useOptimizationGroupQuery(optimizationGroupId)
	const optimizationQuery = useOptimizationQuery(optimizationGroupId, {
		refetchOnMount: 'always',
	})

	const [open, setOpen] = useState(false)
	useEffect(() => {
		open && setOpen(false)
	}, [optimizationGroupId])

	return (
		<div className='flex shrink-0'>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<div className='flex cursor-pointer items-center gap-2'>
						<Tooltip delayDuration={300}>
							<TooltipTrigger asChild>
								<span className='max-w-60 truncate'>
									{optimizationGroupQuery.isLoading ? (
										<Skeleton className='h-6 w-48' />
									) : (
										optimizationGroupQuery.data?.name
									)}
								</span>
							</TooltipTrigger>
							<TooltipContent side='bottom'>
								{optimizationGroupQuery.data?.name}
							</TooltipContent>
						</Tooltip>
						{optimizationQuery.isLoading ? (
							<Skeleton className='h-6 w-12' />
						) : optimizationQuery.data ? (
							<StatusLabel
								status={getOgStatus(optimizationQuery.data.status)}
							/>
						) : null}
					</div>
				</PopoverTrigger>
				<PopoverContent align='start' side='bottom' className='w-auto p-0'>
					<OptimizationGroupsPopoverList />
				</PopoverContent>
			</Popover>
		</div>
	)
}

const StatusLabel = ({ status }: { status: OptimizationStatus }) => {
	const { t } = useTranslation()

	return (
		<Tag
			size='sm'
			className='shrink-0'
			data-testid='optimization-status-label'
			data-status={status}
			variant={getStatusBadgeVariant(status)}
		>
			{t(`opt_${status}`, {
				defaultValue: status,
			})}
		</Tag>
	)
}

function OptimizationGroupsPopoverList() {
	const navigate = useNavigate()
	const optimizationGroupId = useOptimizationGroupId()
	const groups = useOptimizationGroupsQuery()
	const optimizations = useOptimizationsQuery({
		refetchOnMount: 'always',
		select: getOptimizationsMap,
	})
	const isLoading = any(prop('isLoading'), [groups, optimizations])
	const list = groups.data?.map((group) => ({
		value: group.id,
		label: group.name,
		status: optimizations.data?.[group.id]?.status,
	}))
	const options = orderBy(['label'], ['asc'], list)
	const handleSelectOptimizationGroup = (value: string) =>
		navigate({
			to: location.pathname.replace(
				`/p/og/${optimizationGroupId}/`,
				`/p/og/${value}/`
			),
		})

	return (
		<Command className='w-96'>
			<CommandInput placeholder='Search groups...' />
			<CommandList>
				{isLoading ? (
					<CommandEmpty>Loading...</CommandEmpty>
				) : (
					<CommandEmpty>No timezone found.</CommandEmpty>
				)}
				<CommandGroup>
					{options.map((group) => (
						<CommandItem
							key={group.value}
							value={group.value}
							keywords={[group.label]}
							onSelect={(currentValue) => {
								handleSelectOptimizationGroup(currentValue)
							}}
						>
							<Tooltip delayDuration={300}>
								<TooltipTrigger asChild>
									<span className='line-clamp-3 pr-2'>{group.label}</span>
								</TooltipTrigger>
								<TooltipContent>{group.label}</TooltipContent>
							</Tooltip>

							<span className='ml-auto'>
								{!!group.status && (
									<StatusLabel status={getOgStatus(group.status)} />
								)}
							</span>
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}
